import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Tag } from "antd";
import {
  CALL_HISTORY_STATUS,
  STATUS_TYPE,
  USER_TYPE,
} from "constants/appConstant";
import moment from "moment";
import { AuthInfo } from "stores/slices/auth";
import { doGetCallHistoryPage, numOfEls } from "stores/slices/callHistory";
import { setHeaderTitle } from "stores/slices/layout";
import { getUserDetail } from "stores/slices/user";

import AppTable from "components/table";

const CallHistoryPage = () => {
  const dispatch = useDispatch();

  const { t } = useTranslation();
  const authInfo = useSelector(AuthInfo);
  const userInfo = useSelector(getUserDetail);

  const noOfElements = useSelector(numOfEls);

  const [columns, setColumns] = useState([]);

  const [searchParam, setSearchParam] = useState({
    pageNumber: 1,
    pageSize: 10,
    sortDirection: "DESC",
    sorFieldCallHistory: "createdTime",
    statuses: [
      STATUS_TYPE.ACTIVE,
      CALL_HISTORY_STATUS.ANSWERED,
      CALL_HISTORY_STATUS.DISCONNECT,
    ],
  });

  const handleSearch = (selectedKeys, confirm) => {
    confirm();
  };

  const handleFilter = (selectedKeys, confirm) => {
    confirm();
  };

  const handleTableChange = (pagination, filters, sorter) => {
    let newSearchParam = {};

    if (authInfo.type === USER_TYPE.SUPER_ADMIN) {
      filters.workspace &&
        (newSearchParam = {
          ...newSearchParam,
          searchKeyWorkspaceName: filters.workspace[0],
        });
    }

    filters.number &&
      (newSearchParam = {
        ...newSearchParam,
        searchKeyAddress: filters.number[0],
      });

    filters.answeredBy &&
      (newSearchParam = {
        ...newSearchParam,
        searchKeyNumber: filters.answeredBy[0],
      });

    filters.customer &&
      (newSearchParam = {
        ...newSearchParam,
        searchKeyCustomer: filters.customer[0],
      });

    filters.status &&
      (newSearchParam = {
        ...newSearchParam,
        statuses: filters.status,
      });

    filters.amount &&
      filters.amount[0] &&
      (newSearchParam = {
        ...newSearchParam,
        fromPrice: filters.amount[0],
      });
    filters.amount &&
      filters.amount[1] &&
      (newSearchParam = {
        ...newSearchParam,
        toPrice: filters.amount[1],
      });

    if (sorter && sorter.columnKey && sorter.order) {
      newSearchParam = {
        ...newSearchParam,
        sortFieldChannel: sorter.columnKey,
        sortDirection: sorter.order === "ascend" ? "ASC" : "DESC",
      };
    } else {
      newSearchParam = {
        ...newSearchParam,
        sortFieldChannel: "createdTime",
        sortDirection: "DESC",
      };
    }

    newSearchParam = {
      ...newSearchParam,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
    };

    setSearchParam(newSearchParam);
  };

  const handleFormatDateLocale = date => {
    const browserLocale = navigator.language;
    let dateLocale;
    if (date) {
      dateLocale = moment(date)
        .tz(userInfo?.timeZone || moment.tz.guess())
        .locale(browserLocale);
      return `${dateLocale.format("YYYY-MM-DD")} ${dateLocale.format("LT")}`;
    }
  };

  const generateTableColumns = () => {
    let cols = [
      {
        title: t("callHistory.number"),
        width: 50,
        dataIndex: "number",
        key: "number",
        sorter: true,
        isSearchable: true,
        render: (_, { service }) => <>{service?.address || "-"}</>,
      },
      {
        title: t("callHistory.answeredBy"),
        width: 50,
        dataIndex: "answeredBy",
        key: "answeredBy",
        sorter: true,
        isSearchable: true,
        ellipsis: {
          showTitle: true,
        },
        render: (_, { number }) => <>{number || "-"}</>,
      },
      {
        title: t("callHistory.customer"),
        width: 50,
        dataIndex: "customer",
        key: "customer",
        sorter: true,
        isSearchable: true,
        ellipsis: {
          showTitle: true,
        },
        render: (_, { customer }) => (
          <>
            {customer?.firstName || customer?.lastName
              ? `${customer.firstName} ${customer.lastName}`
              : "-"}
          </>
        ),
      },
      {
        title: t("callHistory.amount"),
        width: 50,
        dataIndex: "amount",
        key: "amount",
        isShowPricingRange: true,
        render: (_, { amount, currencyId }) => (
          <>{amount > 0 ? `${currencyId ? currencyId : ""}$${amount}` : "0"}</>
        ),
      },
      {
        title: t("callHistory.result"),
        width: 50,
        dataIndex: "result",
        key: "result",
        render: (_, { result }) => <>{result ? `${result}` : "-"}</>,
      },
      {
        title: t("callHistory.reason"),
        width: 70,
        dataIndex: "reason",
        key: "reason",
        render: (_, { reason }) => <>{reason ? `${reason}` : "-"}</>,
      },
      {
        title: t("callHistory.duration"),
        width: 50,
        dataIndex: "duration",
        key: "duration",
      },
      {
        title: t("callHistory.dateTime"),
        width: 50,
        dataIndex: "createdTime",
        key: "createdTime",
        sorter: true,
        render: (_, record) => (
          <>
            {record.createdTime
              ? handleFormatDateLocale(record.createdTime)
              : "-"}
          </>
        ),
      },
      {
        title: t("callHistory.status"),
        width: 50,
        dataIndex: "status",
        key: "status",
        filters: [
          {
            text: t("status.active"),
            value: STATUS_TYPE.ACTIVE,
          },
          {
            text: t("status.answered"),
            value: CALL_HISTORY_STATUS.ANSWERED,
          },
          {
            text: t("status.disconnect"),
            value: CALL_HISTORY_STATUS.DISCONNECT,
          },
        ],
        render: (_, { status }) => (
          <Tag
            bordered={true}
            color={
              status === STATUS_TYPE.ACTIVE
                ? "#0E9154"
                : status === CALL_HISTORY_STATUS.ANSWERED
                ? "#ffab00"
                : "#A9122B"
            }
          >
            {status.toUpperCase()}
          </Tag>
        ),
      },
    ];

    if (authInfo?.type === USER_TYPE.SUPER_ADMIN) {
      cols = [
        {
          title: t("callHistory.workspace"),
          width: 50,
          dataIndex: "workspace",
          key: "workspace",
          sorter: true,
          isSearchable: true,
          ellipsis: {
            showTitle: true,
          },
          render: (_, { workspace }) => <>{workspace?.name || "-"}</>,
        },
        ...cols,
      ];
    }

    setColumns(cols);
  };

  useEffect(() => {
    dispatch(setHeaderTitle(["callHistory"]));
  }, []);

  useEffect(() => {
    generateTableColumns();
  }, [noOfElements]);

  return (
    <>
      {columns && (
        <AppTable
          searchParam={searchParam}
          api={doGetCallHistoryPage(searchParam)}
          handleTableChange={handleTableChange}
          handleSearch={handleSearch}
          handleFilter={handleFilter}
          columns={columns}
          setSearchParam={setSearchParam}
        />
      )}
    </>
  );
};

export default CallHistoryPage;
